import { BrowserRouter as Router, Routes, Route } from "react-router-dom"
import { Provider } from "react-redux"
import { Toaster } from "react-hot-toast"
import { store } from "./store/store"
import { NotificationProvider } from "./contexts/NotificationContext"
import { HelmetProvider } from "react-helmet-async"

// Layout Components
import Layout from "./components/layout/Layout"
import AdminLayout from "./components/layout/AdminLayout"
import ProtectedRoute from "./components/ProtectedRoute"

// Pages
import Home from "./pages/Home"
import Products from "./pages/Products"
import ProductDetail from "./pages/ProductDetail"
import Cart from "./pages/Cart"
import Checkout from "./pages/Checkout"
import Orders from "./pages/Orders"
import OrderDetail from "./pages/OrderDetail"
import Wishlist from "./pages/Wishlist"
import Profile from "./pages/Profile"
import Search from "./pages/Search"
import Categories from "./pages/Categories"
import Contact from "./pages/Contact"
import Leads from "./pages/Leads"

// Footer Pages
import About from "./pages/About"
import Shipping from "./pages/Shipping"
import Returns from "./pages/Returns"
import FAQ from "./pages/FAQ"

// Policy Pages
import ShippingPolicy from "./pages/ShippingPolicy"
import ReturnPolicy from "./pages/ReturnPolicy"
import RefundCancellationPolicy from "./pages/RefundCancellationPolicy"
import PrivacyPolicy from "./pages/PrivacyPolicy"
import TermsConditions from "./pages/TermsConditions"

// Auth Pages
import Login from "./pages/Auth/Login"
import Signup from "./pages/Auth/Signup"

// Admin Pages
import AdminDashboard from "./pages/Admin/Dashboard"
import AdminProducts from "./pages/Admin/Products"
import AdminOrders from "./pages/Admin/Orders"
import AdminCategories from "./pages/Admin/Categories"
import AdminCustomers from "./pages/AdminCustomers"
import AdminSettings from "./pages/Admin/Settings"
import AddProduct from "./pages/Admin/AddProduct"
import EditProduct from "./pages/Admin/EditProduct"
import AdminBanners from "./pages/Admin/AdminBanners"
import Enquiries from "./pages/Admin/Enquiries"

// Payment Pages
import PaymentCallback from "./pages/PaymentCallback"

// Error Boundary
import ErrorBoundary from "./components/common/ErrorBoundary"

function App() {
  return (
    <Provider store={store}>
      <NotificationProvider>
        <HelmetProvider>
          <Router>
            <ErrorBoundary>
              <div className="App">
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="products" element={<Products />} />
                    <Route path=":category/:productName" element={<ProductDetail />} />
                    <Route path="categories" element={<Categories />} />
                    <Route path="categories/:category" element={<Products />} />
                    <Route path="search" element={<Search />} />
                    
                    {/* Footer Pages */}
                    <Route path="about" element={<About />} />
                    <Route path="shipping" element={<Shipping />} />
                    <Route path="returns" element={<Returns />} />
                    <Route path="faq" element={<FAQ />} />
                    <Route path="contact" element={<Contact />} />  {/* Added Contact page route */}
                    <Route path="leads" element={<Leads />} />  {/* Owner-only, code-gated lead dashboard */}

                    {/* Policy Pages (SEO friendly lowercase URLs) */}
                    <Route path="shipping-policy" element={<ShippingPolicy />} />
                    <Route path="return-policy" element={<ReturnPolicy />} />
                    <Route path="refund-cancellation-policy" element={<RefundCancellationPolicy />} />
                    <Route path="privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="terms-conditions" element={<TermsConditions />} />
                    
                    {/* Auth Pages */}
                    <Route path="login" element={<Login />} />
                    <Route path="signup" element={<Signup />} />

                    {/* Protected User Routes */}
                    <Route
                      path="cart"
                      element={
                        <ProtectedRoute>
                          <Cart />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="checkout"
                      element={
                        <ProtectedRoute>
                          <Checkout />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="orders"
                      element={
                        <ProtectedRoute>
                          <Orders />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="orders/:id"
                      element={
                        <ProtectedRoute>
                          <OrderDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="wishlist"
                      element={
                        <ProtectedRoute>
                          <Wishlist />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="profile"
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                  {/* Payment Callback Route */}
                  <Route path="/payment/callback" element={<PaymentCallback />} />

                  {/* Admin Routes */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="products/add" element={<AddProduct />} />
                    <Route path="products/edit/:id" element={<EditProduct />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="banners" element={<AdminBanners />} />
                    <Route path="enquiries" element={<Enquiries />} />
                  </Route>
                </Routes>

                {/* Toast Notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: "#363636",
                      color: "#fff",
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: "#4ade80",
                        secondary: "#fff",
                      },
                    },
                    error: {
                      duration: 4000,
                      iconTheme: {
                        primary: "#ef4444",
                        secondary: "#fff",
                      },
                    },
                  }}
                />
              </div>
            </ErrorBoundary>
          </Router>
        </HelmetProvider>
      </NotificationProvider>
    </Provider>
  )
}

export default App
